import React from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
} from "@heroui/react";

import "./InfoModal.scss";

type InfoId = 1 | 2 | 3 | 4;

type InfoItem = {
  title: React.ReactNode;
  body: React.ReactNode;
};

const data: Record<InfoId, InfoItem> = {
  1: {
    title: (
      <div className="eos-info-modal__title">
        <div className="eos-info-modal__title-he">תינוקות אסימפטומטיים</div>
        <div className="eos-info-modal__title-en">Well Appearing</div>
      </div>
    ),
    body: (
      <div className="eos-info-modal__body-centered">
        תינוקות עם בדיקה גופנית תקינה.
      </div>
    ),
  },

  2: {
    title: (
      <div className="eos-info-modal__title">
        <div className="eos-info-modal__title-he">
          סיפטומטיים לא ספציפיים לאלח דם
        </div>
        <div className="eos-info-modal__title-en">Equivocal</div>
      </div>
    ),
    body: (
      <div className="eos-info-modal__body-rtl">
        <div className="eos-info-modal__section-title">
          הפרעה בודדת שנמשכת מעל 4 שעות :
        </div>
        <ul className="eos-info-modal__list">
          <li>טכיקרדיה: דופק &gt; 160</li>
          <li>טכיפניאה: נשימות &gt; 60</li>
          <li>חוסר יציבות בחום הגוף: חום פחות מ-C.36.4</li>
          <li>
            סימני מצוקה נשימתית (אנחות, רתיעות בין צלעיות, נשימת כנפי אף) ללא צורך
            בתוספת חמצן.
          </li>
        </ul>

        <div className="eos-info-modal__or">או</div>

        <div className="eos-info-modal__section-title">
          הפרעה בשני סימנים או יותר שנמשכים יותר משעתיים:
        </div>
        <ul className="eos-info-modal__list">
          <li>טכיקרדיה: דופק &gt; 160</li>
          <li>טכיפניאה: נשימות &gt; 60</li>
          <li>חוסר יציבות בחום הגוף: חום פחות מ-C.36.4</li>
          <li>
            סימני מצוקה נשימתית (אנחות, רתיעות בין צלעיות, נשימת כנפי אף) ללא צורך
            בתוספת חמצן.
          </li>
        </ul>
      </div>
    ),
  },

  3: {
    title: "תינוקות עם סימנים משמעותיים",
    body: (
      <ul className="eos-info-modal__list">
        <li>צורך בהמשך תמיכה נשימתית פולשנית או לא פולשנית מחוץ לחדר לידה/ חדר ניתוח</li>
        <li>חוסר יציבות המודינמית, כולל צורך בטיפול תרופתי</li>
        <li>מצב נוירולוגי לא תקין: אנצפלופטיה בילוד, היפוטוניה, חוסר תגובה לגירוי, או פרכוסים</li>
        <li>אפגר נמוך מ5 בגיל 5 דקות</li>
        <li>צורך בהעשרת חמצן למשך יותר משעתיים לאחר הלידה לשמירה על ריווי חמצן מעל 90%</li>
        <li>חום גוף התינוק מעל 38.0</li>
      </ul>
    ),
  },

  4: {
    title: "הטיפול אנטיביוטי",
    body: (
      <ul className="eos-info-modal__list">
        <li>
          במידה והאם קבלה כיסוי טרום לידתי עם אמפיצילין וגנטאמיצין (טיפול משולב) או צפטריאקסון בלבד
          יש להזין שניתנה אנטיביוטיקה רחבת טווח לאם תוך התייחסות למספר שעות טרם לידה שהטיפול ניתן.
        </li>
        <li>
          אם האם קבלה טיפול מסוג beta Lactams - להזין "אנטיביוטיקה ספציפית ל-GBS", במידה וניתן לפחות
          שעתיים טרם הלידה (בקבוצה זו כלולים: פניצילין, אמפיצילין, צפאזולין).
        </li>
        <li>
          אם היולדת לא קיבלה תכשירים מסוג beta Lactams (פניצילין, אמפיצילין, צפאזולין) יש לבחור
          באפשרות "ללא טיפול אנטיביוטי". קלינדאמיצין וונקומיצין אינם נחשבים כיסוי אנטיביוטי הולם.
        </li>
        <li>
          אם היולדת קיבלה כיסוי אנטיביוטי כלשהו פחות משעתיים טרם הלידה יש לבחור באפשרות "ללא טיפול
          אנטיביוטי".
        </li>
      </ul>
    ),
  },
};

export function InfoModal({
  id,
  onClose,
}: {
  id: InfoId | "" | null;
  onClose: () => void;
}) {
  const numericId = id ?? null;
  if (!numericId) return null;

  const { title, body } = data[numericId];

  return (
    <Modal
      isOpen={!!numericId}
      onClose={onClose}
      placement="center"
      className="eos-info-modal"
      hideCloseButton
    >
      <ModalContent>
        {(close) => (
          <>
            {/* Header keeps the colored bar and close button */}
            <ModalHeader className="eos-info-modal__header">
              <div className="eos-info-modal__headerTitle">{title}</div>

              <Button
                isIconOnly
                variant="light"
                onPress={close}
                className="eos-info-modal__closeBtn"
                aria-label="Close"
              >
                ✕
              </Button>
            </ModalHeader>

            {/* Body shows the content and can scroll if long */}
            <ModalBody className="eos-info-modal__body">
              {body}
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}